import {
  getFormDefaultValues,
  getFormInputs,
  getInput,
} from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/components/printingTempBadgeModalContent/PrintingTempBadgeModalContent.forms';
import { ReprintTempBadgeFormData } from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/components/printingTempBadgeModalContent/types';
import { JobsitePrinterOption } from 'containers/utils';
import React from 'react';
import {
  TempBadgeStatus,
  useGetPrintingTempBadgeQuery,
  useReprintTempBadgeMutation,
} from 'apollo/generated/client-operations';
import { AlertService } from 'components/alertService';
import { LoadingError } from 'components/loadingError';
import { getGraphQLError } from 'utils/error';
import { useIsMounted } from 'utils';
import { RedoIcon } from 'components/icons';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { TempBadge } from 'containers/jobsite/tabs/jobsiteTempBadges/types';
import { TempBadgingStep } from 'containers/jobsite/tabs/jobsiteTempBadges/modals/tempBadgeModal/types';

export interface PrintingTempBadgeModalContentProps {
  closeModal: () => void;
  onAction: (
    tempBadge: TempBadge,
    jobsitePrinterOption: JobsitePrinterOption,
  ) => void;
  tempBadge: TempBadge | null;
  lastUsedPrinterOption: JobsitePrinterOption;
  jobsitePrinterOptions: JobsitePrinterOption[];
  defaultJobsitePrinterOption: JobsitePrinterOption | null;
  jobsiteName: string;
  badgingStep:
    | TempBadgingStep.Printing
    | TempBadgingStep.Reprint
    | TempBadgingStep.PrintFailed;
  prevBadgingStep: TempBadgingStep | null;
}

export function PrintingTempBadgeModalContent(
  props: PrintingTempBadgeModalContentProps,
): React.ReactElement {
  const {
    tempBadge,
    lastUsedPrinterOption,
    jobsitePrinterOptions,
    defaultJobsitePrinterOption,
    closeModal,
    onAction,
    badgingStep,
  } = props;
  const isRetry = badgingStep === TempBadgingStep.PrintFailed;

  const formRef = React.useRef<HTMLFormElement>(null);

  const [isFetching, setIsFetching] = React.useState<boolean>(false);
  const isMounted = useIsMounted();

  const [reprintTempBadge] = useReprintTempBadgeMutation();
  const { data: printingTempBadgeData } = useGetPrintingTempBadgeQuery({
    fetchPolicy: 'no-cache',
    pollInterval: tempBadge.status === TempBadgeStatus.Printing ? 4000 : 0,
    variables: { id: tempBadge.id },
  });
  const printingTempBadge = printingTempBadgeData?.getTempBadge;

  // this effect watches for changes in the printing tempBadge status
  // and calls onAction when it changes to update the tempBadge in modal state
  React.useEffect(() => {
    if (printingTempBadge && printingTempBadge.status !== tempBadge?.status) {
      onAction(printingTempBadge, lastUsedPrinterOption);
    }
    // tempBadge.status is not included in this dependency array deliberately.
    // When printing, the tempBadge has a momentary transition to the printFailed
    // status, then correctly moves to the printing status; excluding tempBadge
    // from the dependency array mitigates the UX issue
  }, [printingTempBadge?.status]);

  const onSubmit: FormOnSubmit<ReprintTempBadgeFormData> = async (
    data,
    event,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    try {
      const input = await getInput(tempBadge.id, data);
      const { data: result } = await reprintTempBadge({
        variables: {
          input,
        },
      });
      const reprintedTempBadge = result.reprintTempBadge.tempBadge;
      onAction(reprintedTempBadge, data.jobsitePrinter);
    } catch (error) {
      event?.preventDefault();
      AlertService.alert(
        'danger',
        'Error sending badge print request',
        getGraphQLError(error),
      );
    } finally {
      if (isMounted()) setIsFetching(false);
    }
  };

  const { formInputs, defaultValues } = React.useMemo(() => {
    return {
      formInputs: getFormInputs({ jobsitePrinterOptions, badgingStep }),
      defaultValues: getFormDefaultValues(
        lastUsedPrinterOption,
        defaultJobsitePrinterOption,
      ),
    };
  }, [
    jobsitePrinterOptions,
    lastUsedPrinterOption,
    defaultJobsitePrinterOption,
    badgingStep,
  ]);

  const { statusMessage, status } = tempBadge?.lastPrintJob ?? {
    status: 'Unknown',
  };
  const statusInfo = `Status: ${statusMessage ?? status}`;

  return (
    <ModalFormContent
      ref={formRef}
      setOpen={closeModal}
      renderAbove={
        <div>
          <p className="odin-mb-4">{statusInfo}</p>
          {tempBadge.status === TempBadgeStatus.Printing && (
            <LoadingError noPadding loading />
          )}
        </div>
      }
      inputs={formInputs}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      actionText={isRetry ? 'Try Again' : 'New Print Job'}
      actionIcon={RedoIcon}
      actionButtonWithSpinner={isFetching}
      cancelText="Close"
    />
  );
}
